import React, { useEffect, useState } from "react";
import axios from "axios";
import { Activity, DollarSign, Car, Briefcase, Target, Coins } from "lucide-react";
import { Card } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  withdrawal_paid:   { icon: DollarSign, color: "text-emerald-600 bg-emerald-500/10", label: "Saque" },
  ride_completed:    { icon: Car,        color: "text-blue-600 bg-blue-500/10",       label: "Corrida" },
  ride_accepted:     { icon: Car,        color: "text-primary bg-primary/10",         label: "Corrida" },
  job_completed:     { icon: Briefcase,  color: "text-orange-500 bg-orange-500/10",   label: "Trabalho" },
  mission_claimed:   { icon: Target,     color: "text-purple-500 bg-purple-500/10",   label: "Missão" },
  coin_earned:       { icon: Coins,      color: "text-yellow-500 bg-yellow-500/10",   label: "Moedas" },
};

function timeAgo(iso) {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s atrás`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}min atrás`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h atrás`;
    return `${Math.floor(h / 24)}d atrás`;
  } catch { return ""; }
}

export default function LiveActivityFeed({ limit = 12 }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/activity/recent?limit=${limit}`);
        if (!alive) return;
        setEvents(r.data || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
    const iv = setInterval(load, 10000); // 10s polling
    return () => { alive = false; clearInterval(iv); };
  }, [limit]);

  return (
    <Card className="p-5" data-testid="live-activity-feed">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-500/15 text-emerald-600 flex items-center justify-center relative">
          <Activity className="w-4 h-4" />
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-600">Ao vivo</div>
          <div className="text-sm font-semibold">Atividade da plataforma</div>
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground text-center py-4">Carregando...</div>}
      {!loading && events.length === 0 && (
        <div className="text-sm text-muted-foreground text-center py-4" data-testid="activity-empty">
          Sem atividade recente. Volte em alguns instantes.
        </div>
      )}

      <div className="space-y-1 max-h-[360px] overflow-y-auto pr-1">
        {events.map((e) => {
          const meta = KIND_META[e.kind] || { icon: Activity, color: "text-muted-foreground bg-muted", label: e.kind };
          const Icon = meta.icon;
          return (
            <div
              key={e.event_id}
              className="flex items-start gap-2 p-2 rounded-lg hover:bg-muted/50 transition-colors"
              data-testid={`activity-event-${e.event_id}`}
            >
              <div className={`w-8 h-8 rounded-lg ${meta.color} flex items-center justify-center flex-shrink-0`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs leading-snug">{e.text}</div>
                <div className="text-[10px] text-muted-foreground">{timeAgo(e.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
